'use client';

import { createContext, type ReactNode, useContext, useState } from 'react';

import type { CartOption } from '../_components/AddToCartButton';

interface SelectedOption {
  optionGroupId: number;
  optionItemId: number;
  extraProductPrice: number;
}

interface GiftOptionContextValue {
  /** 선택된 옵션 Map (optionGroupId -> option) */
  selectedOptions: Map<number, SelectedOption>;
  /** 옵션 선택/해제 */
  handleOptionSelect: (
    optionGroupId: number,
    optionItemId: number,
    extraProductPrice: number,
  ) => void;
  /** 옵션 삭제 */
  handleOptionDelete: (optionGroupId: number) => void;
  quantity: number;
  handleQuantityChange: (quantity: number) => void;
  /** 주문용 옵션 배열 반환 */
  getCartOptions: (
    optionGroups: {
      optionGroupId?: number;
      optionGroupName?: string;
      optionItemInfoList?: {
        optionItemId?: number;
        itemName?: string;
        extraProductPrice?: number;
      }[];
    }[],
  ) => CartOption[];
  /** 선택 초기화 */
  resetSelection: () => void;
}

const GiftOptionContext = createContext<GiftOptionContextValue | null>(null);

export const GiftOptionProvider = ({ children }: { children: ReactNode }) => {
  const [selectedOptions, setSelectedOptions] = useState<
    Map<number, SelectedOption>
  >(new Map());
  const [quantity, setQuantity] = useState(1);

  // 옵션 선택 시 (그룹당 하나만 선택, 같은 옵션 클릭 시 선택 해제)
  const handleOptionSelect = (
    optionGroupId: number,
    optionItemId: number,
    extraProductPrice: number,
  ) => {
    setSelectedOptions((prev) => {
      const existing = prev.get(optionGroupId);
      const newMap = new Map(prev);

      // 같은 옵션이 이미 선택되어 있으면 선택 해제
      if (existing?.optionItemId === optionItemId) {
        newMap.delete(optionGroupId);

        return newMap;
      }

      // 그룹 내 다른 옵션 선택 시 교체
      newMap.set(optionGroupId, {
        optionGroupId,
        optionItemId,
        extraProductPrice,
      });

      return newMap;
    });
    // 조합이 바뀌면 단가가 달라지므로 수량을 되돌린다
    setQuantity(1);
  };

  // 옵션 삭제
  const handleOptionDelete = (optionGroupId: number) => {
    setSelectedOptions((prev) => {
      const newMap = new Map(prev);
      newMap.delete(optionGroupId);

      return newMap;
    });
    setQuantity(1);
  };

  // 장바구니용 옵션 정보 변환
  const getCartOptions = (
    optionGroups: {
      optionGroupId?: number;
      optionGroupName?: string;
      optionItemInfoList?: {
        optionItemId?: number;
        itemName?: string;
        extraProductPrice?: number;
      }[];
    }[],
  ): CartOption[] => {
    return Array.from(selectedOptions.values()).map((opt) => {
      const optionGroup = optionGroups.find(
        (g) => g.optionGroupId === opt.optionGroupId,
      );
      const optionItem = optionGroup?.optionItemInfoList?.find(
        (item) => item.optionItemId === opt.optionItemId,
      );

      return {
        optionGroupId: opt.optionGroupId,
        optionGroupName: optionGroup?.optionGroupName ?? '',
        optionItemId: opt.optionItemId,
        optionItemName: optionItem?.itemName ?? '',
        extraProductPrice: opt.extraProductPrice,
      };
    });
  };

  // 선택 초기화
  const resetSelection = () => {
    setSelectedOptions(new Map());
    setQuantity(1);
  };

  const value = {
    selectedOptions,
    handleOptionSelect,
    handleOptionDelete,
    quantity,
    handleQuantityChange: setQuantity,
    getCartOptions,
    resetSelection,
  };

  return (
    <GiftOptionContext.Provider value={value}>
      {children}
    </GiftOptionContext.Provider>
  );
};

export const useGiftOption = () => {
  const context = useContext(GiftOptionContext);
  if (!context) {
    throw new Error('useGiftOption must be used within GiftOptionProvider');
  }

  return context;
};
